import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { withTranslation, WithTranslation } from 'react-i18next';

import ButtonPrimary from '@design-system/ButtonPrimary';
import ButtonNavigationBack from '@design-system/ButtonNavigationBack';
import TitleSection from 'components/TitleSection';

import { useSidebarLayout } from 'providers/root/SidebarLayoutProvider';

import { useViewPanelLayoutStyles } from './styles';
import { handleGoBackNavigation, shouldShowBackButton, shouldShowActionButtons } from './utils';
import { ViewPanelLayoutProps } from './types';
import TopBar from './components/TopBar';

/**
 * ViewPanelLayout component for structured layout with navigation and actions
 *
 * Provides a flexible layout system with:
 * - Header area with optional title and navigation
 * - Action area for buttons and controls
 * - Main content area with consistent spacing
 * - CommandBar integration for marketing automation
 * - Backward compatibility with legacy props
 *
 * Uses the existing TitleSection component for consistency across the application.
 *
 * @param props - Component properties
 * @param props.children - Main content to display
 * @param props.title - Optional title text for header
 * @param props.actionComponent - Optional action component (buttons, etc.)
 * @param props.onGoBack - Handler for back navigation (supports both signatures)
 * @param props.disabledGoBack - Whether back button is disabled
 * @param props.isDisabled - Legacy prop (accepted but ignored for backward compatibility)
 * @param props.disabled - Legacy prop (accepted but ignored for backward compatibility)
 * @param props.commandBarHook - CommandBar integration hook
 * @param props.titleProps - Additional props for TitleSection component
 * @param props.others - Additional Box props
 * @returns JSX.Element ViewPanelLayout component
 *
 * @example
 * <ViewPanelLayout
 *   title="Settings"
 *   onGoBack={() => history.back()}
 *   actionComponent={<Button>Save</Button>}
 *   commandBarHook={useCommandBar()}
 *   titleProps={{ fontSize: 28, color: 'primary.main' }}
 * >
 *   <SettingsForm />
 * </ViewPanelLayout>
 */
function ViewPanelLayout({
  actionComponent: actionComponentProp,
  children,
  disabledGoBack,
  labelAction,
  labelGoBack,
  onAction,
  onGoBack,
  title,
  componentHeader,
  defaultGoBack,
  goBackPath,
  titleProps = {},
  commandBarHook,
  t,
  ...others
}: ViewPanelLayoutProps & WithTranslation): JSX.Element {
  const { openMdDrawer } = useSidebarLayout();
  const theme = useTheme();
  const classes = useViewPanelLayoutStyles({ open: openMdDrawer });

  // Only use disabledGoBack - legacy props (isDisabled, disabled) are accepted but ignored
  const effectiveDisabledGoBack = disabledGoBack || false;

  /**
   * Renders the main content area with proper spacing
   */
  function renderMainContent(): JSX.Element {
    return (
      <Box pl={theme.typography.pxToRem(32)} pr={theme.typography.pxToRem(32)} {...others}>
        {children}
      </Box>
    );
  }

  /**
   * Renders navigation buttons based on configuration
   */
  function renderNavigationButtons(): JSX.Element | null {
    const handleGoBack = (): void => handleGoBackNavigation({ goBackPath });

    if (onGoBack) {
      // Handle both function signatures: () => void and (pathname: string) => void
      const handleClick = (): void => {
        if (onGoBack.length === 0) {
          // Function expects no parameters
          (onGoBack as () => void)();
        } else {
          // Function expects pathname parameter, pass current location or empty string
          (onGoBack as (pathname: string) => void)(window.location.pathname || '');
        }
      };

      return (
        <ButtonNavigationBack data-testid="btnGoBack" disabled={effectiveDisabledGoBack} onClick={handleClick}>
          {labelGoBack}
        </ButtonNavigationBack>
      );
    }

    if (defaultGoBack || goBackPath) {
      return (
        <ButtonNavigationBack data-testid="btnGoBack" disabled={effectiveDisabledGoBack} onClick={handleGoBack}>
          {t('common:buttons.toReturn')}
        </ButtonNavigationBack>
      );
    }

    return null;
  }

  /**
   * Renders action buttons and components
   */
  function renderActionButtons(): JSX.Element | null {
    if (!shouldShowActionButtons(Boolean(actionComponentProp), Boolean(onAction))) {
      return null;
    }

    return (
      <>
        {actionComponentProp && actionComponentProp}
        {onAction && <ButtonPrimary onClick={onAction}>{labelAction}</ButtonPrimary>}
      </>
    );
  }

  /**
   * Renders title using the existing TitleSection component for consistency
   */
  function renderTitle(): JSX.Element | null {
    if (!title) {
      return null;
    }

    return <TitleSection commandBarHook={commandBarHook} title={title} titleProps={titleProps} />;
  }

  /**
   * Renders the top bar content based on current configuration
   */
  function renderTopBarContent(): JSX.Element | null {
    const showBackButton = shouldShowBackButton(Boolean(onGoBack), Boolean(defaultGoBack), Boolean(goBackPath));
    const showActionButtons = shouldShowActionButtons(Boolean(actionComponentProp), Boolean(onAction));

    // Component header mode - no top bar, just header + content
    if (componentHeader) {
      return (
        <>
          <Box mb={6}>{componentHeader}</Box>
          <Box component="main" pb={4} pl={4} pr={4} role="main" {...others}>
            {children}
          </Box>
        </>
      );
    }

    // No title, show navigation and actions
    if (!title) {
      return (
        <TopBar>
          {renderNavigationButtons()}
          {renderActionButtons()}
        </TopBar>
      );
    }

    // Title only, no actions or navigation
    if (title && !showActionButtons && !showBackButton) {
      return <TopBar>{renderTitle()}</TopBar>;
    }

    // Title with actions
    if (title && showActionButtons) {
      return (
        <TopBar>
          {renderTitle()}
          {renderActionButtons()}
        </TopBar>
      );
    }

    // Back navigation with title (separate components)
    if (showBackButton) {
      return (
        <>
          <TopBar>{renderNavigationButtons()}</TopBar>
          {renderTitle()}
        </>
      );
    }

    // Default: title only
    return <TopBar>{renderTitle()}</TopBar>;
  }

  return (
    <Box className={classes.main} component="main" pb={theme.typography.pxToRem(32)} role="main" {...others}>
      {renderTopBarContent()}
      {renderMainContent()}
    </Box>
  );
}

// @ts-ignore
export default withTranslation()(ViewPanelLayout);
